import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Bell, AlertTriangle, AlertCircle, CheckCircle, Clock, 
  Search, Calendar, Filter, MapPin, Users, X, Eye
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const TrackingAlertsPage = () => {
  const { activeBusiness } = useBusiness();
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  useEffect(() => {
    fetchAlerts();
  }, [selectedDate, selectedType]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedDate) params.date = selectedDate;
      if (selectedType) params.type = selectedType;
      const { data } = await api.get('/part3/tracking/alerts', { params });
      setAlerts(data.alerts || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load alerts'));
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await api.post(`/part3/alerts/${alertId}/resolve`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: 'READ' } : a));
      toast.success('Alert marked as read');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to mark alert as read'));
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await api.post(`/part3/alerts/${alertId}/resolve`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert dismissed');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to dismiss alert'));
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'MISSED_VISIT': return <MapPin className="w-4 h-4" />;
      case 'GEOFENCE_EXIT': return <AlertTriangle className="w-4 h-4" />;
      case 'LATE_CHECK_IN': return <Clock className="w-4 h-4" />;
      case 'LOW_BATTERY': return <AlertCircle className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return 'bg-rose-100 text-rose-700 border-rose-200';
      case 'MEDIUM': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'LOW': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const filteredAlerts = alerts.filter(alert =>
    alert.staff_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    alert.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unreadCount = filteredAlerts.filter(a => a.status === 'UNREAD').length;
  const highPriorityCount = filteredAlerts.filter(a => a.severity === 'HIGH').length;
  const mediumPriorityCount = filteredAlerts.filter(a => a.severity === 'MEDIUM').length;

  const alertTypes = [
    { value: 'ALL', label: 'All Types' },
    { value: 'MISSED_VISIT', label: 'Missed Visits' },
    { value: 'GEOFENCE_EXIT', label: 'Geofence Exits' },
    { value: 'LATE_CHECK_IN', label: 'Late Check-ins' },
    { value: 'LOW_BATTERY', label: 'Low Battery' },
  ];

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Page Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Title Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Tracking Alerts</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">Monitor and respond to field staff alerts</p>
            </div>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">
                <Bell className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Alerts</p>
                <p className="text-[14px] font-semibold text-slate-700">{filteredAlerts.length}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">
                <Eye className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Unread</p>
                <p className="text-[14px] font-semibold text-blue-600">{unreadCount}</p>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-rose-50 flex items-center justify-center">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">High Priority</p>
                <p className="text-[14px] font-semibold text-rose-600">{highPriorityCount}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-7 h-7 rounded-md bg-amber-50 flex items-center justify-center">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Medium Priority</p>
                <p className="text-[14px] font-semibold text-amber-600">{mediumPriorityCount}</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search alerts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
            
            <div className="relative flex items-center gap-1">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  placeholder="All dates"
                />
              </div>
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate('')}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Clear date filter"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <Select value={selectedType || "ALL"} onValueChange={(val) => setSelectedType(val === "ALL" ? "" : val)}>
              <SelectTrigger className="w-48 bg-white">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {alertTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-3 border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              </div>
            ) : filteredAlerts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-[15px] font-semibold text-slate-800 mb-1">No alerts found</h3>
                <p className="text-[13px] text-slate-500">All clear! No tracking alerts for this date.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredAlerts.map((alert) => (
                  <div 
                    key={alert.id} 
                    className={`p-5 hover:bg-slate-50/50 transition-colors ${alert.status === 'UNREAD' ? 'bg-blue-50/30' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Alert Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getSeverityColor(alert.severity)}`}>
                        {getAlertIcon(alert.type)}
                      </div>

                      {/* Alert Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${getSeverityColor(alert.severity)}`}>
                                {alert.severity}
                              </span>
                              <span className="text-[11px] text-slate-400">
                                {alert.type?.replace(/_/g, ' ')}
                              </span>
                              {alert.status === 'UNREAD' && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              )}
                            </div>
                            <p className="text-[14px] font-medium text-slate-800 mb-1">{alert.message}</p>
                            <div className="flex items-center gap-3 text-[12px] text-slate-500">
                              <div className="flex items-center gap-1.5">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold ${getInitialColor(alert.staff_name)}`}>
                                  {getInitials(alert.staff_name)}
                                </div>
                                {alert.staff_name}
                              </div>
                              <span>•</span>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {alert.timestamp
                                  ? new Date(alert.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ', ' + new Date(alert.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                  : '—'}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {alert.status === 'UNREAD' && (
                              <button
                                onClick={() => handleMarkAsRead(alert.id)}
                                className="px-3 py-1.5 text-[12px] font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
                              >
                                Mark Read
                              </button>
                            )}
                            <button
                              onClick={() => handleDismiss(alert.id)}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          {filteredAlerts.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-[13px] text-slate-500">
                Showing <span className="font-medium text-slate-700">{filteredAlerts.length}</span> alerts
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackingAlertsPage;
